// src/pages/ClientDashboard.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, 
  Container, CircularProgress, Tooltip, IconButton, TextField, 
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../components/firebase';
import {
  collection, query, where, getDocs, doc, getDoc, addDoc,
  updateDoc, deleteDoc, onSnapshot, serverTimestamp, orderBy, limit as fbLimit
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import CreateForecastForm from '../components/CreateForecastForm';
import CompanyAvatarUploader from '../components/CompanyAvatarUploader';



import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';

import BarChartIcon from '@mui/icons-material/BarChart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import useCryptoMiles from '../hooks/useCryptoMiles';
import { InputAdornment } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IosShareIcon from '@mui/icons-material/IosShare';

import { ensureReferralCode, buildReferralLink } from '../lib/referral';




// === Black Glass tokens  ===
const R12 = '12px';
const GLASS = {
  panel:  'linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.92) 100%)',
  surface:'linear-gradient(180deg, rgba(12,16,22,.92) 0%, rgba(8,11,17,.92) 60%)',
  border: '1px solid rgba(255,255,255,.08)',
  borderStrong: '1px solid rgba(255,255,255,.16)',
  shadow: '0 10px 32px rgba(2,8,23,.45)',
  text: '#e5e7eb',
  subtext: '#9ca3af',
  green: '#34d399',
  red: '#f87171',
};

const RIGHT_MATCH_WIDTH = { xs: '100%', md: '80%', lg: '50%' };

/* ============ Styled ============ */

const Shell = styled(Box)({
  minHeight: '100vh',
});

// === Styled (Black Glass) ===
const Sidebar = styled(Card)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  borderRadius: R12,
  padding: 12,
  background: GLASS.panel,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
}));

const NavItem = styled(Button)(({ active }) => ({
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontWeight: 800,
  borderRadius: R12,
  padding: '10px 12px',
  marginBottom: 8,
  color: GLASS.text,
  border: active ? GLASS.borderStrong : GLASS.border,
  background: active ? 'rgba(255,255,255,.07)' : 'rgba(255,255,255,.04)',
  boxShadow: active ? '0 6px 20px rgba(2,8,23,.35)' : 'none',
  '& .MuiSvgIcon-root': { color: 'inherit' },
  '&:hover': {
    background: 'rgba(255,255,255,.08)',
    transform: 'translateY(-1px)',
    boxShadow: '0 8px 22px rgba(2,8,23,.45)',
  },
}));

const SectionCard = styled(Card)({
  borderRadius: R12,
  background: GLASS.surface,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
});



const HeaderCard = styled(Card)({
  borderRadius: 20,
  boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
  background: 'linear-gradient(90deg, #83cbeb, #a5cdf0, #14F195)'
});

/* ============ Helpers (module-level) ============ */

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');





const PLAN_META = {
  free: { label: 'Free', sx: { bgcolor: '#e3f2fd', color: '#1a237e' } },
  pro: { label: 'Pro', sx: { bgcolor: 'rgba(20,241,149,.18)', color: '#0d8a65' } },
  team: { label: 'Team', sx: { bgcolor: 'rgba(45,116,215,.18)', color: '#1a237e' } },
  ent: { label: 'Enterprise', sx: { bgcolor: '#fff3e0', color: '#e65100' } }
};





// форматтеры
const fmtAssets = (a) => Array.isArray(a) ? (a.length ? a.join(', ') : '—') : (a || '—');
const fmtPool = (v) => `${Number(v || 0)} USDC`;
const fmtDue = (d) => {
  if (!d) return '—';
  try {
    const dt = typeof d === 'string' ? new Date(d) : d.toDate?.() || new Date(d);
    return Number.isNaN(dt?.getTime()) ? String(d) : dt.toISOString().slice(0,10);
  } catch { return String(d); }
};


function StatusChip({ status }) {
  const tone = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning';
  return (
    <Chip
      size="small"
      color={tone}
      label={status}
      sx={{ height: 22, '& .MuiChip-label': { px: .75, lineHeight: '22px', fontSize: 11, fontWeight: 800 } }}
    />
  );
}

function ForecastListCompact({ items, onEdit, onResubmit, onDelete }) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();

  if (!items?.length) {
    return (
      <Typography sx={{ color: GLASS.subtext, px: 1, py: 1.5 }}>
        No forecasts found. Create your first forecast in the menu.
      </Typography>
    );
  }

  // ===== MOBILE: карточки с одинаковыми подписями (Labels === Headers) =====
  if (!mdUp) {
    return (
      <Box sx={{ display: 'grid', gap: 1 }}>
        {items.map((f, i) => (
          <Card
            key={f.id}
            onClick={()=>navigate(`/forecast/${f.id}`)}
            sx={{
              p: 1.25, borderRadius: 1,
              background: 'rgba(255,255,255,.02)',
              border: '1px solid rgba(255,255,255,.10)',
              '&:hover': { background: 'rgba(255,255,255,.04)' },
              cursor: 'pointer'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .5 }}>
              <Typography
                sx={{
                  flex: 1, fontWeight: 800, color: GLASS.text, fontSize: 14,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}
                title={f.title}
              >
                {f.title}
              </Typography>
              <StatusChip status={f.status} />
            </Box>

            {/* key-value список с едиными подписями */}
            <Box sx={{ display: 'grid', gap: .25, fontSize: 12 }}>
              <Box><b style={{color:GLASS.subtext}}>Assets:</b> <span style={{color:GLASS.text}}>{fmtAssets(f.assets)}</span></Box>
              <Box><b style={{color:GLASS.subtext}}>Prediction:</b> <span style={{color:GLASS.text}}>{f.predictionType || '—'}</span></Box>
              <Box><b style={{color:GLASS.subtext}}>Pool:</b> <span style={{color:GLASS.text}}>{fmtPool(f.pool)}</span></Box>
              <Box><b style={{color:GLASS.subtext}}>Deadline:</b> <span style={{color:GLASS.text}}>{fmtDue(f.due)}</span></Box>
            </Box>

            {/* действия */}
            <Box onClick={(e)=>e.stopPropagation()} sx={{ display:'flex', gap:.75, mt: 1, flexWrap:'wrap' }}>
             

              

              {f.status === 'rejected' && (
                <>
                  <Tooltip title="Edit forecast"><IconButton size="small" onClick={()=>onEdit(f)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Resubmit for moderation"><IconButton size="small" onClick={()=>onResubmit(f.id)}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete forecast"><IconButton size="small" onClick={()=>onDelete(f.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </>
              )}
            </Box>
          </Card>
        ))}
      </Box>
    );
  }

  // ===== DESKTOP: cемантическая таблица + фиксированные колонки =====
return (
  <Box sx={{ border:'1px solid rgba(255,255,255,.10)', borderRadius: 1, overflow: 'hidden', bgcolor:'rgba(255,255,255,.02)' }}>
    <Box
      component="table"
      sx={{
        width: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'separate',
        borderSpacing: 0,
        '& th, & td': { p: 1, verticalAlign: 'middle' },
        '& thead th': {
          position: 'sticky', top: 0, zIndex: 1,
          background: 'rgba(255,255,255,.02)', color: GLASS.subtext,
          borderBottom: '1px solid rgba(255,255,255,.08)', fontWeight: 800, fontSize: 12
        },
        '& tbody tr': {
          borderBottom: '1px solid rgba(255,255,255,.06)',
          '&:hover': { background: 'rgba(255,255,255,.03)' }
        }
      }}
    >
      {/* фикс ширины — чтобы всё пиксель-в-пиксель под своими шапками */}
      <colgroup>{[
  { w: '32%' }, // Title
  { w: '20%' }, // Assets
  { w: '14%' }, // Prediction
  { w: '10%' }, // Pool
  { w: '12%' }, // Deadline
  { w: '12%' }, // Status / Actions
].map((c, i) => <col key={i} style={{ width: c.w }} />)}</colgroup>


      <thead>
        <tr>
          <th scope="col">Title</th>
          <th scope="col">Assets</th>
          <th scope="col">Prediction</th>
          <th scope="col">Pool</th>
          <th scope="col">Deadline</th>
          <th scope="col" style={{ textAlign: 'right' }}>Status</th>
        </tr>
      </thead>

      <tbody>
        {items.map((f) => (
          <tr
            key={f.id}
            onClick={() => navigate(`/forecast/${f.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/forecast/${f.id}`);
              }
            }}
            tabIndex={0}
            style={{ cursor: 'pointer', outline: 'none' }}
          >
            {/* Title */}
            <td title={f.title}>
              <Box sx={{
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontWeight: 800, color: GLASS.text, fontSize: 14, lineHeight: 1.2
              }}>
                {f.title}
              </Box>
            </td>

            {/* Assets */}
            <td title={fmtAssets(f.assets)}>
              <Box sx={{
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: GLASS.subtext, fontSize: 13, lineHeight: 1.2
              }}>
                {fmtAssets(f.assets)}
              </Box>
            </td>

            {/* Prediction — строго под своей шапкой */}
            <td>
              <Typography component="span" sx={{ color: GLASS.subtext, fontSize: 13, lineHeight: 1.2 }}>
                {f.predictionType || '—'}
              </Typography>
            </td>

            {/* Pool */}
            <td>
              <Typography component="span" sx={{ color: GLASS.text, fontWeight: 800, fontSize: 13, lineHeight: 1.2 }}>
                {fmtPool(f.pool)}
              </Typography>
            </td>

            {/* Deadline */}
            <td>
              <Typography component="span" sx={{ color: GLASS.subtext, fontSize: 13, lineHeight: 1.2 }}>
                {fmtDue(f.due)}
              </Typography>
            </td>

            {/* Status + (опц.) иконки; клики внутри ячейки не ведут на детальку */}
            <td onClick={(e)=>e.stopPropagation()} style={{ textAlign: 'right' }}>
              <Box sx={{ display:'inline-flex', gap:.75, alignItems:'center', flexWrap:'wrap' }}>
                <StatusChip status={f.status} />

               
                {f.status === 'rejected' && (
                  <>
                    <Tooltip title="Edit forecast">
                      <IconButton size="small" onClick={()=>onEdit(f)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Resubmit for moderation">
                      <IconButton size="small" onClick={()=>onResubmit(f.id)}><RefreshIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete forecast">
                      <IconButton size="small" onClick={()=>onDelete(f.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </td>
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
);

}


function FavoritesList({ items, onRemove }) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();

  if (!items?.length) {
    return <Typography sx={{ color: GLASS.subtext, px: 1, py: 1.5 }}>
      No favorite forecasts yet.
    </Typography>;
  }

  // ===== MOBILE: карточки =====
  if (!mdUp) {
    return (
      <Box sx={{ display: 'grid', gap: 1 }}>
        {items.map((f) => (
          <Card
            key={f.favoriteId}
            onClick={() => navigate(`/forecast/${f.forecastId}`)}
            sx={{
              p: 1.25, borderRadius: 1,
              background: 'rgba(255,255,255,.02)',
              border: '1px solid rgba(255,255,255,.10)',
              '&:hover': { background: 'rgba(255,255,255,.04)' },
              cursor: 'pointer'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .5 }}>
              <Typography
                sx={{
                  flex: 1, fontWeight: 800, color: GLASS.text, fontSize: 14,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}
                title={f.title}
              >
                {f.title}
              </Typography>
              
              <Tooltip title="Remove from favorites">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onRemove(f.favoriteId); }}
                  sx={{ color: GLASS.text }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'grid', gap: .25, fontSize: 12 }}>
              <Box><b style={{color:GLASS.subtext}}>Assets:</b> <span style={{color:GLASS.text}}>{fmtAssets(f.assets)}</span></Box>
              <Box><b style={{color:GLASS.subtext}}>Prediction:</b> <span style={{color:GLASS.text}}>{f.predictionType || '—'}</span></Box>
              <Box><b style={{color:GLASS.subtext}}>Pool:</b> <span style={{color:GLASS.text}}>{fmtPool(f.pool)}</span></Box>
              <Box><b style={{color:GLASS.subtext}}>Deadline:</b> <span style={{color:GLASS.text}}>{fmtDue(f.due)}</span></Box>
            </Box>
          </Card>
        ))}
      </Box>
    );
  }

  // ===== DESKTOP: таблица =====
  return (
    <Box sx={{ border:'1px solid rgba(255,255,255,.10)', borderRadius: 1, overflow: 'hidden', bgcolor:'rgba(255,255,255,.02)' }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          borderCollapse: 'separate',
          borderSpacing: 0,
          '& th, & td': { p: 1, verticalAlign: 'middle', height: 44 },
          '& thead th': {
            position: 'sticky', top: 0, zIndex: 1,
            background: 'rgba(255,255,255,.02)', color: GLASS.subtext,
            borderBottom: '1px solid rgba(255,255,255,.08)', fontWeight: 800, fontSize: 12
          },
          '& tbody tr': {
            borderBottom: '1px solid rgba(255,255,255,.06)',
            '&:hover': { background: 'rgba(255,255,255,.03)' }
          }
        }}
      >
        <colgroup>{[
  { w: '32%' }, // Title
  { w: '20%' }, // Assets
  { w: '14%' }, // Prediction
  { w: '10%' }, // Pool
  { w: '12%' }, // Deadline
  { w: '12%' }, // Actions
].map((c, i) => <col key={i} style={{ width: c.w }} />)}</colgroup>


        <thead>
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Assets</th>
            <th scope="col">Prediction</th>
            <th scope="col">Pool</th>
            <th scope="col">Deadline</th>
            <th scope="col" style={{ textAlign: 'right' }}>Status</th>
          </tr>
        </thead>

        <tbody>
          {items.map((f) => (
            <tr
              key={f.favoriteId}
              onClick={() => navigate(`/forecast/${f.forecastId}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/forecast/${f.forecastId}`);
                }
              }}
              tabIndex={0}
              style={{ cursor: 'pointer', outline: 'none' }}
            >
              <td title={f.title}>
                <Box sx={{
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontWeight: 800, color: GLASS.text, fontSize: 14, lineHeight: 1.2
                }}>
                  {f.title}
                </Box>
              </td>
              <td title={fmtAssets(f.assets)}>
                <Box sx={{
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: GLASS.subtext, fontSize: 13, lineHeight: 1.2
                }}>
                  {fmtAssets(f.assets)}
                </Box>
              </td>
              <td><Typography component="span" sx={{ color: GLASS.subtext, fontSize: 13, lineHeight: 1.2 }}>{f.predictionType || '—'}</Typography></td>
              <td><Typography component="span" sx={{ color: GLASS.text, fontWeight: 800, fontSize: 13, lineHeight: 1.2 }}>{fmtPool(f.pool)}</Typography></td>
              <td><Typography component="span" sx={{ color: GLASS.subtext, fontSize: 13, lineHeight: 1.2 }}>{fmtDue(f.due)}</Typography></td>

              <td onClick={(e)=>e.stopPropagation()} style={{ textAlign: 'right' }}>
                <Box sx={{ display:'inline-flex', gap:.75, alignItems:'center', flexWrap:'wrap' }}>
                  
                  <Tooltip title="Remove from favorites">
                    <IconButton size="small" onClick={() => onRemove(f.favoriteId)} sx={{ color: GLASS.text }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}




/* ============ Main ============ */

const ClientDashboard = () => {
  const [user] = useAuthState(auth);

  const [userData, setUserData] = useState(null);
  const { total: milesTotal, pending: milesPending, events, loading: milesLoading } = useCryptoMiles(user?.uid);

  const [ setBonuses] = useState([]);
  const [ setTotalMiles] = useState(0);

  const [forecasts, setForecasts] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [ setTasksCompleted] = useState(0);
  const [ setEarnedTokens] = useState(0);
  const [milesWalletDisp, setMilesWalletDisp] = useState(null);

  const [loading, setLoading] = useState(true);
  const [ setForecastFormOpen] = useState(false); 
  const [editingForecast, setEditingForecast] = useState(null);

  const [plan, setPlan] = useState('free');
  const [ setPlanUntil] = useState(null); // Date | null
  const [ setLimits] = useState({
    minute: null, // { limit, remaining, reset: Date|null }
    day: null
  });

  const formRef = useRef(null);
  const [active, setActive] = useState('prpay'); 
  // какой раздел активен


// referral
const [refCode, setRefCode]   = useState('');
const [refLink, setRefLink]   = useState('');
const [refLoading, setRefLoading] = useState(false);


  // Fetch basic data
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        await user.getIdToken(true);

        // 1) profile from Firestore
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const userDocData = userSnap.exists() ? userSnap.data() : null;
        if (userDocData) setUserData(userDocData);
        // кошелёк для Crypto Miles
       let wallet = userDocData?.walletAddress || null;
       if (!wallet) {
         // fallback: берём из последнего milesEvents
         const meRef = collection(db, 'users', user.uid, 'milesEvents');
         const meSnap = await getDocs(query(meRef, orderBy('createdAt', 'desc'), fbLimit(1)));
         const last = meSnap.docs?.[0]?.data();
         wallet = last?.meta?.wallet || null;
       }
       setMilesWalletDisp(wallet);

        // 2) brands
        const tr = await user.getIdTokenResult(true);
        const claims = tr?.claims || {};

        // 3) plan/term/limits
        const planFromClaims = claims.plan || null;
        const planFromDoc = userDocData?.plan || null;
        const effectivePlan = (planFromClaims || planFromDoc || 'free').toLowerCase();
        setPlan(effectivePlan);

        setPlanUntil(claims.plan_exp ? new Date(claims.plan_exp * 1000) : null);

        const mLimit = claims.rl_minute_limit ?? null;
        const mRem = claims.rl_minute_remaining ?? null;
        const mReset = claims.rl_minute_reset ? new Date(claims.rl_minute_reset * 1000) : null;

        const dLimit = claims.rl_day_limit ?? null;
        const dRem = claims.rl_day_remaining ?? null;
        const dReset = claims.rl_day_reset ? new Date(claims.rl_day_reset * 1000) : null;

        setLimits({
          minute: mLimit != null ? { limit: Number(mLimit), remaining: Number(mRem ?? 0), reset: mReset } : null,
          day: dLimit != null ? { limit: Number(dLimit), remaining: Number(dRem ?? 0), reset: dReset } : null
        });

        // 4) bonuses
        const bonusesQuery = query(collection(db, 'clientBonuses'), where('userId', '==', user.uid));
        const bonusesSnapshot = await getDocs(bonusesQuery);
        const bonusesData = bonusesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBonuses(bonusesData);
        setTotalMiles(bonusesData.reduce((sum, b) => sum + (b.cryptoMiles || 0), 0));

        // 5)tasks
        const tasksQuery = query(collection(db, `users/${user.uid}/tasks`), where('status', '==', 'completed'));
        const tasksSnapshot = await getDocs(tasksQuery);
        setTasksCompleted(tasksSnapshot.docs.length);

        // 6) tokens
        const tokensQuery = query(collection(db, `users/${user.uid}/tokens`));
        const tokensSnapshot = await getDocs(tokensQuery);
        setEarnedTokens(tokensSnapshot.docs.reduce((sum, d) => sum + (d.data().balance || 0), 0));
      } catch (err) {
        toast.error('Failed to load profile: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // live forecasts
    const forecastsQuery = query(
      collection(db, 'promos'),
      where('userId', '==', user.uid),
      where('isForecast', '==', true)
    );
    const unsubF = onSnapshot(
      forecastsQuery,
      (qs) => {
        setForecasts(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (e) => toast.error('Error fetching forecasts: ' + e.message)
    );

    // live favorites
   const favQuery = query(collection(db, `users/${user.uid}/favorites`));
const unsubFav = onSnapshot(
  favQuery,
  (qs) => {
    const rows = qs.docs.map((d) => {
      const data = d.data() || {};
      const task = data.task || {};
      // если снапшота нет, используем promoId или сам id документа
      const fallbackId = data.promoId || d.id;
      return {
        favoriteId: d.id,                 // id записи избранного
        forecastId: task.id || fallbackId, // id прогноза для навигации
        title: task.title || "",           // остальное — безопасные дефолты
        assets: Array.isArray(task.assets) ? task.assets : [],
        predictionType: task.predictionType || "",
        pool: task.pool ?? 0,
        due: task.due || "",
        status: task.status || "",
      };
    });
    setFavorites(rows);
  },
  (e) => toast.error('Error fetching favorites: ' + e.message)
);


    return () => {
      unsubF();
      unsubFav();
    };
  }, [user, setBonuses, setEarnedTokens, setLimits, setPlanUntil, setTasksCompleted, setTotalMiles]);

  // actions
  const handleEditForecast = (forecast) => {
    setEditingForecast(forecast);
    setActive('create');
    setForecastFormOpen(true);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        formRef.current.style.transition = 'background-color 0.5s';
        formRef.current.style.backgroundColor = '#e3f2fd';
        setTimeout(() => (formRef.current.style.backgroundColor = 'transparent'), 1000);
      }
    }, 200);
  };


  const handleRemoveFavorite = async (favoriteId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return toast.error('User not authenticated');

    await deleteDoc(doc(db, `users/${currentUser.uid}/favorites`, favoriteId));
    toast.success('Removed from favorites');
    setFavorites((prev) => prev.filter((f) => f.favoriteId !== favoriteId));
  } catch (e) {
    toast.error('Error removing favorite: ' + e.message);
  }
};


  const handleDeleteForecast = async (forecastId) => {
    if (!window.confirm('Delete this forecast?')) return;
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return toast.error('User not authenticated');

      await currentUser.getIdToken(true);
      await deleteDoc(doc(db, 'promos', forecastId));
      await addDoc(collection(db, 'promoHistory'), {
        promoId: forecastId,
        userId: currentUser.uid,
        action: 'delete',
        timestamp: serverTimestamp()
      });
      toast.success('Forecast deleted!');
      setForecasts((prev) => prev.filter((x) => x.id !== forecastId));
    } catch (e) {
      toast.error('Error deleting forecast: ' + e.message);
    }
  };

  const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text || '');
    toast.success('Copied to clipboard');
  } catch (e) {
    toast.error('Copy failed');
  }
};

const share = async () => {
  if (!refLink) return;
  try {
    if (navigator.share) {
      await navigator.share({
        title: 'Join me on PromosPay',
        text: refCode ? `Use my code: ${refCode}` : '',
        url: refLink,
      });
    } else {
      await copy(refLink);
    }
  } catch (_) {}
};

const loadReferral = async () => {
  if (!user || refLoading) return;
  setRefLoading(true);
  try {
    const u = await getDoc(doc(db, 'users', user.uid));
    const d = u.exists() ? u.data() : {};
    let code = d?.referralCode;
    if (!code) {
      // We'll instantly create a code (it will create /referralCodes/{code} and write it to users/{uid})
      code = await ensureReferralCode(user.uid);
    }
    setRefCode(code);
    const link = typeof buildReferralLink === 'function'
      ? buildReferralLink(code)
      : `${window.location.origin}/?ref=${encodeURIComponent(code)}`; // backup option
    setRefLink(link);
  } catch (e) {
    toast.error('Failed to load referral: ' + (e?.message || 'Unknown error'));
  } finally {
    setRefLoading(false);
  }
};


  const handleRetryModeration = async (forecastId) => {
    try {
      await updateDoc(doc(db, 'promos', forecastId), { status: 'pending', moderation: null });
      await addDoc(collection(db, 'promoHistory'), {
        promoId: forecastId,
        userId: user.uid,
        action: 'resubmit',
        timestamp: new Date().toISOString()
      });
      toast.success('Resubmitted for moderation!');
      setForecasts((prev) =>
        prev.map((c) => (c.id === forecastId ? { ...c, status: 'pending', moderation: null } : c))
      );
    } catch (e) {
      toast.error('Error resubmitting: ' + e.message);
    }
  };

  if (loading) return <Typography sx={{ p: 4 }}>Loading…</Typography>;
  if (!user || !userData) return <Typography sx={{ p: 4 }}>User not found.</Typography>;

    return (
    <Shell>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* ======= Header: Personal Info  ======= */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <HeaderCard
  sx={{
    p: { xs: 1.25, sm: 1.75 },
    mb: 3,
    width: { xs: '100%', md: '58%' },   
    background: GLASS.panel,
    border: GLASS.border,
    borderRadius: R12,
    boxShadow: GLASS.shadow,
    color: GLASS.text,
  }}
>
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '56px 1fr auto',
      columnGap: 1.25,
      rowGap: 1,
      alignItems: 'center',
    }}
  >
    {/* LEFT: Round avatar + download button below*/}
    <Box sx={{ gridColumn: '1', gridRow: '1 / span 2', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 56, height: 56, minWidth: 56,
          borderRadius: '50%', overflow: 'hidden',
          border: GLASS.border, boxShadow: '0 6px 18px rgba(2,8,23,.35)',
          '& img, & .MuiAvatar-root': { borderRadius: '50% !important', width: '100%', height: '100%' },
        }}
      >
        <CompanyAvatarUploader
  userId={user.uid}
  avatarUrl={userData.avatar}
  onUpload={(url) => setUserData((p) => ({ ...p, avatar: url }))}

  size={56}
  compact
  deletable
  showDeleteOverlay
  circleSx={{
    border: GLASS?.border || "1px solid rgba(255,255,255,.12)",
    boxShadow: "0 6px 18px rgba(2,8,23,.35)",
  }}
/>
      </Box>
      <Button
        size="small"
        variant="outlined"
        sx={{
          height: 26, minHeight: 26, px: 1,
          borderRadius: 1, fontSize: 11, fontWeight: 800, textTransform: 'none',
          borderColor: 'rgba(255,255,255,.28)', color: GLASS.text,
          '&:hover': { borderColor: 'rgba(255,255,255,.4)', bgcolor: 'rgba(255,255,255,.06)' },
        }}
        onClick={() => {/* If the uploader is clickable, you can omit it; otherwise, attach a trigger here.*/}}
      >
        Upload
      </Button>
    </Box>

    {/* CENTER: Personal Info + email */}
    <Box sx={{ gridColumn: { xs: '2 / 4', sm: '2' }, gridRow: '1', minWidth: 240 }}>
      <Typography
        sx={{
          fontFamily: "'Bruno Ace SC', sans-serif",
          fontSize: { xs: 14, sm: 16 }, fontWeight: 800, lineHeight: 1.1, color: GLASS.text,
        }}
      >
        Personal Info
      </Typography>
      <Typography variant="body2" sx={{ color: GLASS.subtext, mt: 0.25 }}>
        {userData.email}
      </Typography>
    </Box>

    {/* BOTTOM ROW: Crypto miles Wallet on the left, Plan + Upgrade on the right */}
    <Box
      sx={{
        gridColumn: { xs: '2 / 4', sm: '2 / 4' },
        gridRow: '2',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      {/*  Crypto miles Wallet */}
      <Typography variant="body2" sx={{ color: GLASS.text, mr: 'auto' }}>
        Crypto miles Wallet:{' '}
 <Box component="span" sx={{ color: GLASS.subtext }}>
   {milesWalletDisp ? shortAddr(milesWalletDisp) : 'Not connected'}
 </Box>
      </Typography>

      {/* Plan + Upgrade/Billing  */}
      <Chip
        label={`Plan: ${(PLAN_META[plan]?.label || plan).toUpperCase()}`}
        size="small"
        sx={{
          height: 22, borderRadius: 1,
          px: 0.5, fontWeight: 800,
          bgcolor: 'rgba(255,255,255,.06)', border: GLASS.border, color: GLASS.text,
          '& .MuiChip-label': { px: 0.75, fontSize: 11, lineHeight: '22px', fontWeight: 800 },
        }}
      />

      {plan === 'free' ? (
        <Button
          variant="contained"
          component={Link}
          to="/pricing"
          startIcon={<UpgradeIcon />}
          sx={{
            height: 28, minHeight: 28, px: 1.25, borderRadius: 1,
            fontSize: 12, fontWeight: 800, textTransform: 'none',
            background: 'linear-gradient(90deg,#2d74d7 0%,#14F195 100%)',
          }}
        >
          Upgrade
        </Button>
      ) : (
        <Button
          variant="outlined"
          component={Link}
          to="/billing"
          startIcon={<ManageAccountsIcon />}
          sx={{
            height: 28, minHeight: 28, px: 1.25, borderRadius: 1,
            fontSize: 12, fontWeight: 800, textTransform: 'none',
            borderColor: 'rgba(255,255,255,.28)', color: GLASS.text,
            '&:hover': { borderColor: 'rgba(255,255,255,.4)', bgcolor: 'rgba(255,255,255,.06)' },
          }}
        >
          Billing
        </Button>
      )}
    </Box>
  </Box>
</HeaderCard>


        </motion.div>

        {/* ======= Body: Sidebar + Content ======= */}
        <Grid container spacing={3}>
          {/* Left nav */}
          <Grid item xs={12} md={4} lg={3}>
            <Sidebar>
              <Typography
                variant="subtitle2"
                sx={{ fontFamily: "'Bruno Ace SC', sans-serif", color: GLASS.subtext, mb: 1 }}
              >
                Menu
              </Typography>

              

              <NavItem fullWidth active={active === 'miles' ? 1 : 0} onClick={() => setActive('miles')} startIcon={<StarIcon />}>
                Your Crypto Miles
              </NavItem>



              <NavItem
                fullWidth
                active={active === 'create' ? 1 : 0}
                onClick={() => {
                  setActive('create');
                  setForecastFormOpen(true);
                  setEditingForecast(null);
                  setTimeout(() => {
                    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 150);
                }}
                startIcon={<AddCircleOutlineIcon />}
              >
                Create a New Forecast
              </NavItem>

              <NavItem fullWidth active={active === 'my' ? 1 : 0} onClick={() => setActive('my')} startIcon={<BarChartIcon />}>
                Your Forecasts
              </NavItem>

              <NavItem fullWidth active={active === 'fav' ? 1 : 0} onClick={() => setActive('fav')} startIcon={<FavoriteIcon />}>
                Favorites
              </NavItem>
              <NavItem
  fullWidth
  active={active === 'ref' ? 1 : 0}
  onClick={() => {
    setActive('ref');
    if (!refCode) loadReferral();
  }}
  startIcon={<LinkIcon />}>
  Referral
</NavItem>

            </Sidebar>
          </Grid>

          {/* Right content */}
          <Grid item xs={12} md={8} lg={9}>
          



            {/* Crypto Miles */}
            {active === 'miles' && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <SectionCard sx={{ p: 3, mb: 3, width: RIGHT_MATCH_WIDTH }}>
  <CardContent>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
      <StarIcon sx={{ color: GLASS.text, opacity: .9 }} />
      <Typography variant="h6" sx={{ fontFamily: "'Bruno Ace SC', sans-serif", color: GLASS.text }}>
        Your Crypto Miles
      </Typography>
    </Box>

    <Typography variant="h5" sx={{ color: GLASS.text, mb: 1 }}>
      Total: {milesLoading ? '—' : milesTotal} CrM
      {milesPending ? <Typography component="span" sx={{ color: GLASS.subtext, ml: 1 }}>(Pending: {milesPending})</Typography> : null}
    </Typography>

    {/* latest events*/}
    {events.length > 0 ? (
      <Box sx={{ mt: 1 }}>
        {events.slice(0, 6).map((e) => (
          <Box key={e.id} sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            p: 1, borderRadius: 1, mb: .5,
            background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)'}}
          >
            <Typography sx={{ color: GLASS.text, fontSize: 14, fontWeight: 700, textTransform: 'capitalize' }}>
              {e.source?.replace(/_/g,' ') || 'event'}
            </Typography>
            <Typography sx={{ color: e.status === 'pending' ? GLASS.subtext : GLASS.text, fontWeight: 800 }}>
              +{e.points} {e.status === 'pending' ? '(pending)' : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : (
      <Typography sx={{ color: GLASS.subtext }}>
        No Crypto Miles yet. Earn them in{' '}
        <Link to="/crypto-miles" style={{ color: GLASS.text, fontWeight: 700 }}>
          Crypto Miles
        </Link>{' '}or via activity on the platform.
      </Typography>
    )}

    <Button
      variant="contained"
      component={Link}
      to="/crypto-miles"
      sx={{
        mt: 1.5,
        background: 'linear-gradient(90deg, #2d74d7 0%, #14F195 100%)',
        textTransform: 'none',
        fontWeight: 700
      }}
    >
      Earn More
    </Button>
  </CardContent>
</SectionCard>

              </motion.div>
            )}

            {/* Create Forecast */}
            {active === 'create' && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <SectionCard ref={formRef} sx={{ p: 3, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
  <AddCircleOutlineIcon sx={{ color: GLASS.text, opacity: .9 }} />
  <Typography
    variant="h6"
    sx={{ fontFamily: "'Bruno Ace SC', sans-serif", color: GLASS.text }}
  >
    Create a New Forecast
  </Typography>
</Box>

                    <CreateForecastForm
                      userId={user?.uid}
                      onCreated={() => {
                        setEditingForecast(null);
                        toast.success('Forecast created!');
                      }}
                      editData={editingForecast}
                      onCloseEdit={() => {
                        setEditingForecast(null);
                      }}
                    />
                  </CardContent>
                </SectionCard>
              </motion.div>
            )}

        {/* Referral */}
{active === 'ref' && (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
    <SectionCard sx={{ p: 3, mb: 3, width: RIGHT_MATCH_WIDTH }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <LinkIcon sx={{ color: GLASS.text, opacity: .9 }} />
          <Typography variant="h6" sx={{ fontFamily: "'Bruno Ace SC', sans-serif", color: GLASS.text }}>
            Referral
          </Typography>
        </Box>

        <Typography sx={{ color: GLASS.subtext, mb: 2 }}>
          Share your personal link or code to invite friends.
        </Typography>

        {/* Code */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            p: 1, borderRadius: 1, mb: 1.5,
            background: 'rgba(20,241,149,.12)',
            border: '1px solid rgba(20,241,149,.45)'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontWeight: 900, letterSpacing: .5, color: '#14F195', fontSize: 18
            }}
          >
            {refLoading ? '—' : (refCode || '—')}
          </Typography>
          <Tooltip title="Copy code">
            <span>
              <IconButton size="small" onClick={() => copy(refCode)} disabled={!refCode || refLoading}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Link*/}
        <TextField
          label="Your referral link"
          value={refLink || ''}
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Copy link">
                  <span>
                    <IconButton onClick={() => copy(refLink)} disabled={!refLink || refLoading} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Share">
                  <span>
                    <IconButton onClick={share} disabled={!refLink || refLoading} size="small">
                      <IosShareIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            )
          }}
          sx={{
            mb: 2,
            '& label': { color: GLASS.subtext },
            '& label.Mui-focused': { color: '#14F195' },
            '& .MuiOutlinedInput-root': {
              background: 'rgba(45,116,215,.12)',
              borderRadius: 1,
              '& fieldset': { borderColor: 'rgba(45,116,215,.35)' },
              '&:hover fieldset': { borderColor: 'rgba(45,116,215,.55)' },
              '&.Mui-focused fieldset': { borderColor: '#14F195' }
            },
            '& .MuiInputBase-input': {
              color: '#14F195',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontWeight: 800,
              wordBreak: 'break-all'
            },
            '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: GLASS.text }
          }}
        />

    
      </CardContent>
    </SectionCard>
  </motion.div>
)}




           {active === 'my' && (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
    <SectionCard sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
      <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
        <Typography variant="h6" sx={{ fontFamily: "'Bruno Ace SC', sans-serif", color: GLASS.text, mb: 1 }}>
          Your Forecasts
        </Typography>

        {loading ? (
          <Box sx={{ display:'flex', justifyContent:'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ForecastListCompact
            items={forecasts}
            onEdit={handleEditForecast}
            onResubmit={handleRetryModeration}
            onDelete={handleDeleteForecast}
          />
        )}
      </CardContent>
    </SectionCard>
  </motion.div>
)}






            {active === 'fav' && (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
    <SectionCard sx={{ p: 3, mb: 3 }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{ fontFamily: "'Bruno Ace SC', sans-serif", color: GLASS.text, mb: 2 }}
        >
          Your Favorite Forecasts
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FavoritesList items={favorites} onRemove={handleRemoveFavorite} />
        )}
      </CardContent>
    </SectionCard>
  </motion.div>
)}

            
          </Grid>
        </Grid>
      </Container>

      <ToastContainer position="top-right" autoClose={3000} />
    </Shell>
  );
};

export default ClientDashboard